import * as React from 'react';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import { WorkspaceField, WorkspaceInput, WorkspaceSelect } from '@/components/ui/workspace/field';
import { errorMessage, errorReason, useCreateVendor } from '@/lib/api/vendor-knowledge';
import { ApiError } from '@/lib/api/client';
import { Actions, FormError, Linkish, Page, PageHead } from './kb-bits';
import { PLATFORM_TYPES } from './kb-logic';
import { cleanHost } from './catalog-edit';
import { useKb } from './vendor-knowledge-screen';

/**
 * Add a vendor by hand (architect's decision, 2026-10-06; prototype
 * `newVendorView()`): its name, platform type and first web domain. The
 * server refuses a name already in the knowledge base (vendors and partners
 * share names, retired ones included) and any protected client name; the
 * vendor's page opens next with the steps that fill it.
 */
export function NewVendorPage() {
  const { navigate, tree } = useKb();
  const { toast } = useToast();
  const create = useCreateVendor();
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState('');
  const [domain, setDomain] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [existing, setExisting] = React.useState<{ id: string; kind: string } | null>(null);

  const save = () => {
    const clean = name.trim().replace(/\s+/g, ' ');
    const host = cleanHost(domain);
    setExisting(null);
    if (clean.length < 2) return setError('Type the vendor’s name.');
    if (!type) return setError('Pick its platform type.');
    setError(null);
    create.mutate(
      {
        name: clean,
        platform_type: type,
        ...(host ? { domains: [{ host, include_subdomains: true }] } : {}),
      },
      {
        onSuccess: (out) => {
          toast({ message: `Added ${clean}. Its page shows the steps that fill it.` });
          navigate({ kind: 'vendor', id: out.id });
        },
        onError: (err) => {
          setError(errorMessage(err));
          if (errorReason(err) === 'vk.name_taken' && err instanceof ApiError) {
            const id = err.details?.['existing_id'];
            const kind = err.details?.['existing_kind'];
            if (typeof id === 'string' && typeof kind === 'string') setExisting({ id, kind });
          }
        },
      },
    );
  };

  const openExisting = () => {
    if (!existing) return;
    const known =
      existing.kind === 'partner'
        ? tree.partners.some((p) => p.id === existing.id)
        : tree.vendors.some((v) => v.id === existing.id);
    if (known)
      navigate({ kind: existing.kind === 'partner' ? 'partner' : 'vendor', id: existing.id });
  };

  return (
    <Page>
      <PageHead title="Add a vendor">
        A company whose products the knowledge base should hold. Its page opens next, with the steps
        that fill it.
      </PageHead>
      <form
        className="mt-6 max-w-[560px] rounded-card border border-ink-100 bg-paper-0 px-6 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <WorkspaceField label="Vendor name">
          {(id) => (
            <WorkspaceInput
              id={id}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="The company’s name"
              autoComplete="off"
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Platform type" className="mt-4">
          {(id) => (
            <WorkspaceSelect id={id} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="" disabled>
                Pick one
              </option>
              {PLATFORM_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </WorkspaceSelect>
          )}
        </WorkspaceField>
        <WorkspaceField
          label="First web domain"
          className="mt-4"
          help="Pages on it are captured without extra approval. You can add more domains later."
        >
          {(id, describedBy) => (
            <WorkspaceInput
              id={id}
              aria-describedby={describedBy}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              autoComplete="off"
            />
          )}
        </WorkspaceField>
        {error ? (
          <div className="mt-3.5">
            <FormError>
              {error} {existing ? <Linkish onClick={openExisting}>Open it</Linkish> : null}
            </FormError>
          </div>
        ) : null}
        <Actions>
          <WorkspaceButton type="submit" variant="primary" disabled={create.isPending}>
            Add vendor
          </WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={() => navigate({ kind: 'home' })}>
            Cancel
          </WorkspaceButton>
        </Actions>
      </form>
    </Page>
  );
}
